/**
 * The import-advice request's copy, in one place because two pages ask for it.
 *
 * It used to live in `pages/contact/content.ts`, which was the right home
 * while /contact was the only page with the form on it. It is not any more:
 * `/free-import-advice` asks the same questions, carries the same reassurance
 * and sends the same enquiry, and copy duplicated across two pages is copy
 * that drifts — the band would still say "nothing is booked" months after the
 * page it shares a form with had been reworded.
 *
 * ## Why the file is still called `consultation`
 *
 * The offer is now worded as free advice, because "book a consultation"
 * promised a diary entry the site cannot make and asked a visitor for a
 * commitment before they had been given anything. The *service* is still
 * "Import consultation" — one of the services Kabex named (§59), with its own
 * page at `/services/import-consultation` — and the enquiries this form sends
 * still carry `kind: 'consultation'`. The marketing name and the domain name
 * are allowed to differ, and this is the seam between them.
 *
 * Nothing here is a new business claim. "Free, and not conditional on buying"
 * and the description of what to send are Kabex's own wording from the service
 * entry in `content/services.ts`; the rest restates §32 and A16.
 *
 * The page's own path is not here. It is a route the chrome links to, so it is
 * `consultationCta` in `config/navigation.ts` beside `quoteCta` — and keeping
 * it there is what stops this file and the navigation config forming a cycle.
 */

import { type NextStep } from '@/components/molecules';
import { type ContentItem } from '@/content/site';

export const CONSULTATION_WHATSAPP_MESSAGE =
  'Hello Kabex Motors, I would like free advice about importing a car.';

/**
 * The frame's own words: what the form is called, what it asks for, and what
 * the button says.
 *
 * `submitLabel` is first person and names the outcome. "Book a Consultation"
 * was an obligation verb attached to a meeting; "Get My Free Advice" is the
 * thing the visitor actually leaves with, and the two words that carry the
 * objection — free, and mine — are both in it.
 *
 * It no longer matches the header pill word for word, and that is the one
 * place the two are allowed to differ: the pill is an invitation at the top of
 * every page, the button is the last thing read before a decision. Both say
 * "free advice", so the offer is recognisably the same one.
 */
export const consultationForm = {
  title: 'Get free advice',
  /**
   * No count in the description, deliberately. It said "four questions" while
   * the card showed five boxes — four required and one optional — and a form
   * that miscounts itself in its own first sentence is the same small
   * over-claim the old "book a consultation" wording was retired for. The
   * visitor can see how long it is; the sentence is better spent on what they
   * get back.
   */
  description:
    'Tell us what you are considering and how to reach you, and we will come back to you with what we would do — including when the answer is "not that car".',
  submitLabel: 'Get My Free Advice',
} as const;

/**
 * What happens to a request after it is sent, in the order it happens. It is
 * the rail beside the form rather than text under the button because the doubt
 * it answers — "what am I committing to?" — is the reason a visitor hesitates
 * over the submit, so it has to be readable while they fill the form in.
 *
 * None of the four mentions a time or a slot any more. The site has no
 * calendar and invents no availability (A16, §32), and a step promising to
 * "confirm the time" was the rail quietly reintroducing the booking the form
 * had just stopped offering.
 */
export const consultationSteps: readonly NextStep[] = [
  {
    title: 'You tell us what you are considering',
    text: 'Nothing is paid or committed by asking. You get a reference number straight away.',
  },
  {
    title: 'We check it against the rules',
    text: 'The age limit, the environmental levy band, and how URA values that model and year in its own database.',
  },
  {
    title: 'We come back to you',
    text: 'On whichever channel you chose, with a written summary so you are not relying on memory.',
  },
  {
    title: 'You decide with the numbers in front of you',
    text: 'If you want to proceed we move to a quotation. If not, nothing is owed and nothing has started.',
  },
];

export const consultationAssurance = {
  eyebrow: 'What happens next',
  title: 'Free, and no obligation',
  /** The one line a visitor is owed beside a submit button (§39). */
  privacy:
    'We use your details to send the advice and nothing else. No deposit, no reservation, no obligation to buy — people use this to sanity-check a car they are buying elsewhere.',
} as const;

/**
 * The four ways the answer can come back, described.
 *
 * The same four the form's `meetingType` offers (`meetingTypes` in
 * `schemas/enquiry.ts`), in the same order, with a line each. They are content
 * rather than a second source of truth: the select is still driven by the
 * schema, and this list only explains what choosing one means.
 *
 * WhatsApp leads, which is the order the schema was reordered into. It is the
 * channel Kabex actually answers on (§28) and the one most people pick; the
 * office visit is last because it is the only one that costs the visitor a
 * trip across Kampala.
 */
export const meetingWays: readonly ContentItem[] = [
  {
    icon: 'chat',
    title: 'On WhatsApp',
    text: 'Voice notes, photos and figures in writing, so you keep a record of what was said and can read it back later.',
  },
  {
    icon: 'phone',
    title: 'On a phone call',
    text: 'The quickest option, and the one most people take. Fifteen minutes is usually enough to know whether a car is worth importing.',
  },
  {
    icon: 'team',
    title: 'On a video call',
    text: 'For the questions that need a screen between us — comparing two cars, or walking through a cost breakdown line by line.',
  },
  {
    icon: 'building',
    title: 'In person, in Kampala',
    text: 'Crown House on Kampala Road. Bring whatever you have — a link, a photo, an auction sheet — and we go through it at the desk with you.',
  },
];
